import keyBy from 'lodash/keyBy';

import { useTranslation } from '@lyearn/i18n';
import { Stack, Typography } from '@lyearn/ui';
import RichTextViewer from '@/components/RichTextViewer';
import { MtfElement } from '@/types/schema';

import { FIRST_KEYCODE_INDEX } from '../../components/Player/OptionsListItem/consts';
import ReviewIcon from '../../components/QuestionReview/ReviewIcon';
import AnswerMap from '../../fill-in-the-blanks/QuestionReview/AnswerMap';
import { OptionsListItemTypeEnum } from '../../single-select/types';
import { QuestionReviewProps } from '../../types';
import { getElementListItemType } from '../utils';
import CorrectAnswers from './CorrectAnswers';

const QuestionReview: React.FC<QuestionReviewProps> = ({
  question,
  correctAnswers,
  questionAttempt,
}) => {
  const { t } = useTranslation();

  const mtfComponent = question.mtfComponent;
  const mtfResponse = questionAttempt?.submission?.mtfResponse || [];
  const correctMtfPairings = correctAnswers?.correctMTFPairings;
  const isQuestionSubmitted = Boolean(questionAttempt?.isSubmitted);
  const rightElementsById = keyBy(mtfComponent?.distractedRightElements || [], 'id');

  const correctAnswerCount = mtfComponent?.leftElements.filter(
    (element) =>
      getElementListItemType({
        element: element as MtfElement,
        isQuestionSubmitted,
        mtfResponse,
        correctMtfPairings,
        side: 'left',
      }).type === OptionsListItemTypeEnum.Correct,
  ).length;

  const showCorrectAnswers =
    isQuestionSubmitted &&
    correctMtfPairings &&
    mtfComponent?.leftElements?.length !== correctAnswerCount;

  return (
    <>
      {mtfResponse.length ? (
        mtfComponent?.leftElements.map((component, index) => {
          const type = getElementListItemType({
            element: component as MtfElement,
            isQuestionSubmitted,
            mtfResponse,
            correctMtfPairings,
            side: 'left',
          });

          const rightElementResponse = type.responsePair?.rightElementIds;
          const responseText = rightElementResponse?.length
            ? rightElementsById[rightElementResponse[0]]?.text
            : undefined;

          return (
            <Stack key={component.id} className="px-8" gapX={12}>
              <ReviewIcon
                keyCode={String.fromCharCode(FIRST_KEYCODE_INDEX + index)}
                type={
                  [OptionsListItemTypeEnum.Default, OptionsListItemTypeEnum.Answered].includes(
                    type.type,
                  )
                    ? OptionsListItemTypeEnum.Selected
                    : type.type
                }
              />
              <AnswerMap
                labelLeft={<RichTextViewer html={component.text} variant="body-short-02" />}
                labelRight={
                  responseText ? (
                    <RichTextViewer html={responseText} variant="body-short-02" />
                  ) : undefined
                }
              />
              {isQuestionSubmitted && type.type === OptionsListItemTypeEnum.Missed ? (
                <Typography color="text-text-success" variant="body-short-02">
                  {t('QuizPlayer:Missed')}
                </Typography>
              ) : null}
            </Stack>
          );
        })
      ) : (
        <Typography className="ms-8" color="text-text-placeholder" variant="label-02">
          {t('QuizPlayer:NotAnswered')}
        </Typography>
      )}
      {showCorrectAnswers ? (
        <CorrectAnswers correctMtfPairings={correctMtfPairings} mtfComponent={mtfComponent} />
      ) : null}
    </>
  );
};

export default QuestionReview;
